import _ from 'lodash';
import axios from 'axios';
import { FC, memo } from 'react';
import { ListUniqueTagsRequestData } from '../../../pages/api/listUniqueTags';
import { Option } from '@morf/ui/Dropdown/types';
import { ProviderFiltersProps } from './types';
import { scheduling } from '@morf/proto/scheduling/v1/scheduling_ts_proto';
import { useQuery } from '@tanstack/react-query';
import { useUpdateUrlParam } from '@morf/ui/Hooks/useUpdateUrlParam';
import { useUrlParams } from '../../../components/helpers/useUrlParams';
import { useResizeHandler } from '../../../components/helpers/useResizeHandler';
import {
  Button,
  Container,
  Dropdown,
  Flexbox,
  FlexItem,
  SkeletonLoadable,
  Text,
  useResize,
} from '@morf/ui';

const ProviderFilters: FC<ProviderFiltersProps> = ({ content }) => {
  const {
    hideInsurance,
    hideSpecialties,
    hideState,
    insurance,
    morfOrgId,
    patientId,
    specialties,
    state,
  } = useUrlParams();

  const updateUrlParam = useUpdateUrlParam();

  const { data: uniqueTags, isLoading: uniqueTagsIsLoading } =
    useQuery<scheduling.v1.UniqueTags>(
      ['/scheduling.v1.SchedulingService/ListUniqueTags', morfOrgId, patientId],
      async () => {
        const requestData: ListUniqueTagsRequestData = {
          morfOrgId,
          patientId,
        };
        const response = await axios.post('/api/listUniqueTags', requestData);
        return response.data;
      }
    );

  const geographicLocalesAsOptions: Option[] =
    uniqueTags?.geographicLocales?.map((geographicLocale) => ({
      label: geographicLocale,
      value: geographicLocale,
    })) || [];

  const defaultGeographicLocalesAsOption: Option[] =
    geographicLocalesAsOptions.filter(({ value }) => value === state);

  const insuranceTypesAsOptions: Option[] =
    uniqueTags?.insuranceTypes?.map((insuranceType) => ({
      label: insuranceType.insurancePayer!.name,
      value: insuranceType.insurancePayer!.id,
    })) || [];

  const defaultInsuranceTypeAsOption: Option[] = insuranceTypesAsOptions.filter(
    ({ value }) => value === insurance
  );

  const specialtiesAsOptions: Option[] =
    uniqueTags?.specialties?.map((specialty) => ({
      label: specialty,
      value: specialty,
    })) || [];

  const defaultSpecialtiesAsOptions: Option[] = specialtiesAsOptions.filter(
    ({ value }) => specialties?.includes(value)
  );

  const showFilters: boolean = !hideState || !hideInsurance || !hideSpecialties;

  const handleStateChange = (states: Option[]) => {
    const state = states[0];
    updateUrlParam({ state: state.value });
  };

  const handleInsuranceChange = (insurances: Option[]) => {
    const insurance = insurances[0];
    updateUrlParam({ insurance: insurance.value });
  };

  const handleSpecialtiesChange = (specialties: Option[]) => {
    updateUrlParam({
      specialties: specialties.map((s) => s.value),
    });
  };

  const handleClearFilters = () => {
    updateUrlParam({ state: null, insurance: null, specialties: null });
  };

  useResizeHandler([content]);

  const responsiveFilters = useResize({
    direction: {
      xxl: 'row',
      xl: 'row',
      lg: 'row',
      md: 'row',
      sm: 'column',
      xs: 'column',
    },
    maxWidth: {
      xxl: '33.3%',
      xl: '33.3%',
      lg: '33.3%',
      md: '33.3%',
      sm: '100%',
      xs: '100%',
    },
    contentOrder: {
      xxl: 1,
      xl: 1,
      lg: 1,
      md: 1,
      sm: 2,
      xs: 2,
    },
    clearFiltersOrder: {
      xxl: 2,
      xl: 2,
      lg: 2,
      md: 2,
      sm: 1,
      xs: 1,
    },
  });

  return (
    <Flexbox
      data-testid='provider-filters'
      direction='column'
      justifyContent='flex-start'
      alignItems='center'
      height='auto'
      gap={0}
    >
      <Flexbox
        justifyContent='flex-start'
        direction={responsiveFilters.direction}
        px={1.25}
        pt={showFilters ? 1.25 : 0}
      >
        {!hideState && (
          <Container maxWidth={responsiveFilters.maxWidth}>
            <SkeletonLoadable isLoading={uniqueTagsIsLoading} height='2.25rem'>
              <Dropdown
                key={state}
                placeholder='State'
                options={geographicLocalesAsOptions}
                defaultOptions={defaultGeographicLocalesAsOption}
                onChange={handleStateChange}
                height='2.25rem'
                searchable
                clearable
              />
            </SkeletonLoadable>
          </Container>
        )}

        {!hideInsurance && (
          <Container maxWidth={responsiveFilters.maxWidth}>
            <SkeletonLoadable isLoading={uniqueTagsIsLoading} height='2.25rem'>
              <Dropdown
                key={insurance}
                placeholder='Insurance'
                options={insuranceTypesAsOptions}
                defaultOptions={defaultInsuranceTypeAsOption}
                onChange={handleInsuranceChange}
                height='2.25rem'
                searchable
                clearable
              />
            </SkeletonLoadable>
          </Container>
        )}

        {!hideSpecialties && (
          <Container maxWidth={responsiveFilters.maxWidth}>
            <SkeletonLoadable isLoading={uniqueTagsIsLoading} height='2.25rem'>
              <Dropdown
                key={_.toString(specialties)}
                placeholder='Specialty'
                options={specialtiesAsOptions}
                defaultOptions={defaultSpecialtiesAsOptions}
                onChange={handleSpecialtiesChange}
                height='2.25rem'
                searchable
                clearable
                multiSelect
              />
            </SkeletonLoadable>
          </Container>
        )}
      </Flexbox>

      <Flexbox
        p={1.25}
        direction={responsiveFilters.direction}
        justifyContent='space-between'
        alignItems='center'
        gap={showFilters ? 1 : 0}
      >
        <FlexItem flexOrder={responsiveFilters.contentOrder} flexGrow={1}>
          {content}
        </FlexItem>

        <FlexItem flexOrder={responsiveFilters.clearFiltersOrder}>
          {showFilters && (
            <Button
              variant='tertiary'
              isDisabled={!state && !insurance && !specialties}
              onClick={handleClearFilters}
            >
              <Text tag='h5' color='inherit' whiteSpace='nowrap'>
                Clear filters
              </Text>
            </Button>
          )}
        </FlexItem>
      </Flexbox>
    </Flexbox>
  );
};

export const MemoizedProviderFilters = memo(ProviderFilters);
